import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { ChevronDown, Plus, Building2 } from "lucide-react";

const COMMON_BANKS = [
  "SBI",
  "HDFC Bank",
  "ICICI Bank",
  "Axis Bank",
  "Kotak Mahindra Bank",
  "Bank of Baroda",
  "Punjab National Bank",
  "Canara Bank",
  "Union Bank of India",
  "IndusInd Bank",
  "Yes Bank",
  "IDFC First Bank",
  "Federal Bank",
  "South Indian Bank",
  "Indian Bank",
  "Bank of India",
  "Central Bank of India",
  "UCO Bank",
  "Indian Overseas Bank",
  "Karnataka Bank",
  "Google Pay",
  "PhonePe",
  "Paytm",
  "GPay",
];

const STORAGE_KEY = "xale_custom_bank_names";

function getStoredBanks(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function persistBank(name: string) {
  const existing = getStoredBanks();
  if (!existing.includes(name) && !COMMON_BANKS.includes(name)) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...existing, name]));
  }
}

interface BankNameInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  usedBankNames?: string[];
  className?: string;
}

export default function BankNameInput({
  value,
  onChange,
  placeholder = "Bank name",
  usedBankNames = [],
  className = "",
}: BankNameInputProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(value);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setSearch(value);
  }, [value]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const allBanks = useMemo(() => {
    const set = new Set<string>(COMMON_BANKS);
    getStoredBanks().forEach((b) => {
      if (b) set.add(b);
    });
    usedBankNames.forEach((b) => {
      if (b) set.add(b);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [usedBankNames]);

  const filtered = useMemo(() => {
    if (!search.trim()) return allBanks;
    const q = search.toLowerCase();
    return allBanks.filter((b) => b.toLowerCase().includes(q));
  }, [allBanks, search]);

  const exactMatch = allBanks.some(
    (b) => b.toLowerCase() === search.trim().toLowerCase()
  );

  const handleSelect = useCallback(
    (name: string) => {
      onChange(name);
      setSearch(name);
      persistBank(name);
      setOpen(false);
    },
    [onChange]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setSearch(v);
    onChange(v);
    if (!open) setOpen(true);
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <Building2 className="absolute left-2 top-1/2 -translate-y-1/2 size-3 text-[#9ba3a0] pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={handleInputChange}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="h-7 w-full pl-7 pr-6 text-[11px] rounded-[6px] border border-[#e2e4e3] bg-white focus:border-[#46a580] focus:outline-none transition-colors"
        />
        <button
          type="button"
          onClick={() => {
            setOpen(!open);
            inputRef.current?.focus();
          }}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 size-4 flex items-center justify-center text-[#9ba3a0] hover:text-[#697571]"
        >
          <ChevronDown
            className={`size-3 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {open && (
        <div className="absolute z-50 top-full mt-0.5 left-0 right-0 max-h-[180px] overflow-y-auto rounded-[8px] border border-[#e2e4e3] bg-white shadow-lg">
          {filtered.length === 0 && !search.trim() && (
            <p className="px-2.5 py-2 text-[10px] text-[#9ba3a0]">
              Type to search banks...
            </p>
          )}
          {filtered.map((bank) => (
            <button
              key={bank}
              type="button"
              onClick={() => handleSelect(bank)}
              className={`w-full text-left px-2.5 py-1.5 text-[11px] hover:bg-[#f0faf5] transition-colors flex items-center gap-2 ${
                bank === value
                  ? "bg-[#f0faf5] text-[#267355] font-medium"
                  : "text-[#374741]"
              }`}
            >
              <Building2 className="size-3 text-[#9ba3a0] shrink-0" />
              {bank}
            </button>
          ))}
          {search.trim() && !exactMatch && (
            <button
              type="button"
              onClick={() => handleSelect(search.trim())}
              className="w-full text-left px-2.5 py-1.5 text-[11px] hover:bg-[#f0faf5] transition-colors flex items-center gap-2 text-[#267355] font-medium border-t border-[#f0f1f0]"
            >
              <Plus className="size-3 shrink-0" />
              Add "{search.trim()}"
            </button>
          )}
        </div>
      )}
    </div>
  );
}
